/**
 * Cliente HTTP do painel super-admin (F3-05).
 *
 * Autenticação por COOKIE de sessão (Sanctum stateful — o domínio painel.* está
 * em SANCTUM_STATEFUL_DOMAINS). Toda requisição vai com `credentials: 'include'`
 * e o header X-XSRF-TOKEN derivado do cookie XSRF-TOKEN. Antes de mutações,
 * garante-se o cookie CSRF via GET /sanctum/csrf-cookie.
 */

const API_ROOT = '/api/v1';

export type ApiError = Error & {
    status: number;
    data: Record<string, unknown>;
    /** Erros de validação Laravel (422), por campo. */
    errors?: Record<string, string[]>;
};

function readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
}

let csrfReady = false;

async function ensureCsrfCookie(): Promise<void> {
    if (csrfReady && readCookie('XSRF-TOKEN')) {
        return;
    }
    await fetch('/sanctum/csrf-cookie', { credentials: 'include' });
    csrfReady = true;
}

async function request<T>(method: string, path: string, body?: unknown, rawBody?: FormData): Promise<T> {
    const isMutation = method !== 'GET' && method !== 'HEAD';
    if (isMutation) {
        await ensureCsrfCookie();
    }

    const headers: Record<string, string> = {
        Accept: 'application/json',
    };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) {
        headers['X-XSRF-TOKEN'] = xsrf;
    }

    const res = await fetch(API_ROOT + path, {
        method,
        headers: rawBody ? { Accept: 'application/json', ...(xsrf ? { 'X-XSRF-TOKEN': xsrf } : {}) } : headers,
        credentials: 'include',
        body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });

    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (!res.ok) {
        const err = new Error((payload.message as string) ?? 'Falha na requisição') as ApiError;
        err.status = res.status;
        err.data = payload;
        err.errors = payload.errors as Record<string, string[]> | undefined;
        throw err;
    }

    return payload as T;
}

export const api = {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    postForm: <T>(path: string, form: FormData) => request<T>('POST', path, undefined, form),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
};
